import { Revision, iOrder } from "/app/src/models";
import { revisionService } from "/app/src/services";
import Table from "/app/src/components/generic/tables/table";
import { useMemo, useState } from "react";
import { useSortUpgrade } from "/app/src/hooks/useSortUpgrade";
import { useTranslation } from "react-i18next";
import { createColumnHelper } from "@tanstack/react-table";
import DateTime from "/app/src/components/generic/formatting/dateTime";
import usePaginatedData from "/app/src/hooks/usePaginatedData";

/**
 * Table with the revisions of an order
 */
export default function Revisions({ order }: { order: iOrder }) {
  const { t } = useTranslation();
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [sort, setSort] = useSortUpgrade([]);

  const {
    data: revisions,
    count: revisionsCount,
    isFetching,
  } = usePaginatedData<Revision>({
    queryKey: ["revisions"],
    page,
    pageSize,
    sort,
    service: revisionService,
    options: { orderId: order.id },
    refetchInterval: 10000,
  });

  const columnHelper = createColumnHelper<Revision>();
  const columns = useMemo(
    () => [
      columnHelper.accessor("lineId", {
        id: "lineId",
        cell: (info) =>
          info.getValue() === null ? "Order" : `Line ${info.getValue()}`,
        header: t("translation:type"),
      }),
      columnHelper.accessor("previous", {
        id: "previous",
        cell: (info) => info.getValue(),
        header: t("translation:previous"),
      }),
      columnHelper.accessor("to", {
        id: "to",
        cell: (info) => info.getValue(),
        header: t("translation:to"),
      }),
      columnHelper.accessor("creationDate", {
        id: "creationDate",
        cell: (info) => <DateTime date={info.getValue()} />,
        header: t("translation:date"),
      }),
      columnHelper.accessor("userName", {
        id: "userName",
        cell: (info) => info.getValue(),
        header: t("translation:user"),
      }),
    ],
    [columnHelper, t],
  );
  return (
    <Table
      loading={isFetching}
      rows={revisions}
      tableColumns={columns}
      length={revisionsCount}
      sort={sort}
      setSort={setSort}
      paginationEnabled={{
        currentPage: page,
        pageSize,
        setPage,
        setPageSize,
      }}
      emptyText={t("translation:no_revisions_found")}
    />
  );
}
